import { useTranslation } from "next-i18next";
import React from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { HeartIconFill } from "@/features/common/components/icons/HeartIconFill";
import { HeartIconOutlined } from "@/features/common/components/icons/HeartIconOutlined";
import { ImageCardLikeButton } from "@/features/post/components/postCard/components/ImageCardLikeButton";
import { ImageCardRemoveLikeButton } from "@/features/post/components/postCard/components/ImageCardRemoveLikeButton";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { useLikeQuery } from "@/generated/generated-hooks";

interface PostLikeButtonProps {
  likes: number;
  postId: string;
}

export const PostLikeButton: React.FC<PostLikeButtonProps> = (props) => {
  const { user } = useUserContext();
  const { postId, likes } = props;

  const { data } = useLikeQuery({
    variables: { input: postId },
    skip: !user,
  });

  return (
    <>
      {data?.like ? (
        <ImageCardRemoveLikeButton
          postId={postId}
          logEvent={AnalyticsEventEnum.PostPageRemoveLike}
        >
          <Label likes={likes} isRemoveLikeIcon />
        </ImageCardRemoveLikeButton>
      ) : (
        <ImageCardLikeButton
          postId={postId}
          logEvent={AnalyticsEventEnum.PostPageLike}
        >
          <Label likes={likes} isRemoveLikeIcon={false} />
        </ImageCardLikeButton>
      )}
    </>
  );
};

const Label = ({
  likes,
  isRemoveLikeIcon,
}: {
  likes: number;
  isRemoveLikeIcon: boolean;
}) => {
  const { t } = useTranslation(["post", "common"]);

  return (
    <div className="flex items-center">
      {isRemoveLikeIcon ? (
        <HeartIconFill
          className="text-primary w-6 h-6"
          alt="remove like icon"
        />
      ) : (
        <HeartIconOutlined className="text-gray-300 w-6 h-6" alt="like icon" />
      )}
      <div className="flex px-2">
        {likes === 0 && (
          <span className="text-sm text-gray-500">{t("like")}</span>
        )}
        {likes > 0 && (
          <span className="text-sm text-gray-500">{`${new Intl.NumberFormat().format(
            likes,
          )} ${likes > 1 ? t("likes") : t("like")}`}</span>
        )}
      </div>
    </div>
  );
};
